package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.site.api.AuthProviders
import dev.lingvo.shared.AcquisitionSource
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import kotlinx.browser.window
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.ViewBox
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.css.px

/**
 * "Continue with Google".
 *
 * A full page navigation rather than a popup or an embedded Google button: the flow is a redirect
 * chain that ends on our own callback, and popups are blocked, awkward on mobile, and impossible to
 * resume if the window is closed halfway. `window.location.assign` also means the browser treats
 * this as a normal navigation, so the back button behaves.
 *
 * [redirectTo] is where the user lands afterwards. The server refuses anything that is not a
 * same-site path, so a tampered link cannot turn sign-in into a redirect to somewhere else.
 */
@Composable
fun GoogleSignInButton(
    label: String = "Continue with Google",
    redirectTo: String = "/app",
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
    acquisitionSource: AcquisitionSource = AcquisitionSource.UNKNOWN,
) {
    // Whether this deployment has OAuth configured at all. Offering a button that leads to a 404
    // is worse than offering none: the user cannot tell a broken product from a broken account.
    when (AuthProviders.googleAvailable) {
        // Still unknown. Hold the space so the form beneath does not jump when the answer lands.
        null -> {
            Div(Modifier.fillMaxWidth().height(GOOGLE_BUTTON_HEIGHT.px).toAttrs())
            return
        }

        false -> return
        true -> Unit
    }

    LButton(
        label = label,
        onClick = {
            val target = googleSignInUrl(ApiConfig.baseUrl, redirectTo, acquisitionSource)
            window.location.assign(target)
        },
        modifier = modifier,
        variant = LButtonVariant.SECONDARY,
        enabled = enabled,
        leadingIcon = { GoogleGlyph() },
    )
}

/**
 * Google's mark, in its own four brand colours.
 *
 * The one place in the product where a colour outside the palette is allowed: Google's terms
 * require the logo be reproduced faithfully, and a monochrome or ochre-tinted "G" is both a
 * trademark problem and instantly readable as a knock-off button.
 *
 * The 18px is Google's number too, and stays written out rather than reaching for [IconSize.LG],
 * which happens to hold the same value. They are equal by coincidence, not by agreement — retuning
 * our own scale must not silently restyle somebody else's trademark.
 */
@Composable
private fun GoogleGlyph() {
    Svg(attrs = Modifier.width(18.px).height(18.px).toAttrs {
        viewBox(ViewBox.sized(48, 48))
        attr("aria-hidden", "true")
        attr("focusable", "false")
    }) {
        listOf(
            "#EA4335" to "M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z",
            "#4285F4" to "M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z",
            "#FBBC05" to "M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z",
            "#34A853" to "M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z",
        ).forEach { (colour, path) ->
            Path {
                d(path)
                attr("fill", colour)
            }
        }
    }
}

private fun encodeURIComponent(value: String): String =
    js("encodeURIComponent(value)") as String

internal fun googleSignInUrl(baseUrl: String, redirectTo: String, source: AcquisitionSource): String =
    baseUrl.removeSuffix("/") + "/auth/google/start?redirect=" + encodeURIComponent(redirectTo) +
        if (source == AcquisitionSource.UNKNOWN) "" else "&source=" + encodeURIComponent(source.tag)

/** Matches LButtonSize.MD, so reserving the space cannot drift from the button that fills it. */
private const val GOOGLE_BUTTON_HEIGHT = 40
